"use client";

import { SentryFeatureTags, logException } from "@punchup/sentry";
import { SpecialRow, getSpecialByID } from "@/data/specials";

import { getUserCanAccessContent } from "@punchup/data/legacy";
import { useQuery } from "@tanstack/react-query";
import { useUserContentAccess } from "./useUserContentAccess";

interface UseSpecialUnlockedForUserOptions {
  specialId: string;
  userId: string | null | undefined;
  /** Optionally pass the special to avoid fetching it, e.g. in case we've already retrieved it on the server */
  special?: SpecialRow;
}

/**
 * Hook to check if a specific special is unlocked for a given user.
 * Uses shared user-level access data (via useUserContentAccess) so that
 * multiple instances on the same page don't duplicate user data fetches.
 *
 * @param specialId - The ID of the special to check
 * @param userId - The user's ID (null if not logged in)
 * @param special - Optional SpecialRow to avoid refetching the special
 */
export function useSpecialUnlockedForUser({
  specialId,
  userId,
  special: specialProp,
}: UseSpecialUnlockedForUserOptions) {
  const { data: accessData, isLoading: accessLoading } = useUserContentAccess({
    userId,
  });

  const {
    data: fetchedSpecial,
    isLoading: specialLoading,
    error: specialError,
  } = useQuery({
    queryKey: ["special", specialId],
    queryFn: async () => {
      try {
        return await getSpecialByID(specialId);
      } catch (error) {
        logException(error as Error, {
          extra: {
            data: "Error fetching special in useSpecialUnlockedForUser hook",
          },
          tags: {
            supabase: true,
            feature: SentryFeatureTags.Specials,
            specialId,
          },
        });
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !specialProp,
  });

  const special = specialProp ?? fetchedSpecial;
  const isLoading =
    userId === undefined || (!specialProp && specialLoading) || accessLoading;

  if (isLoading || !special) {
    return { unlocked: false, isLoading, error: specialError };
  }

  if (!userId) {
    const unlocked = getUserCanAccessContent({
      special,
      userFollowsComedian: false,
      userPurchasedSpecial: false,
      isUserSubscribed: false,
    });
    return { unlocked, isLoading: false, error: null };
  }

  const userMaxSubscriptionTier =
    accessData?.tiersByComedianId[special.comedian_id] ?? 0;

  const unlocked = getUserCanAccessContent({
    special,
    userFollowsComedian:
      accessData?.followedComedianIds.includes(special.comedian_id) ?? false,
    userPurchasedSpecial:
      accessData?.paidSpecialIds.includes(specialId) ?? false,
    isUserSubscribed:
      accessData?.subscribedComedianIds.includes(special.comedian_id) ?? false,
    userMaxSubscriptionTier,
  });

  return { unlocked, isLoading: false, error: null };
}
